import React, { FC } from 'react';
import { useTranslation, useEnvironment } from '@wix/yoshi-flow-editor';
import { Camera } from '@wix/wix-ui-icons-common/on-stage';
import { Avatar } from 'wix-ui-tpa';
import { MediaPlatformImage } from '@wix/members-domain-ts';

import {
  DataHook,
  ElementId,
  InjectedClassNameProps,
  Nullable,
  ProfileImage,
  ProfileImageSize,
  ProfileLayout,
} from '../../../types';
import styles from './AvatarInput.scss';
import classNames from 'classnames';
import { getElementId } from '../../../services/html-attributes';
import { acceptableImageExtensions } from '../../../constants/image';

type AvatarProps = {
  url: Nullable<string | MediaPlatformImage>;
  imageType: ProfileImage;
  avatarSize?: ProfileImageSize;
  profileLayout?: ProfileLayout;
  canUpload?: boolean;
  onClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  isMobile?: boolean;
} & InjectedClassNameProps;

const AvatarInput: FC<AvatarProps> = ({
  url,
  imageType,
  className = '',
  name,
  onClick,
  canUpload,
  avatarSize,
  profileLayout,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const { widgetId } = useEnvironment();
  const inputId = getElementId(widgetId, ElementId.ProfilePhotoInput);
  const imageURL = typeof url === 'object' ? url?.file_name : url;

  if (imageType === ProfileImage.None) {
    return null;
  }

  const isAvatarSquare = imageType === ProfileImage.Square;

  const sizeClasses = {
    [styles.small]: avatarSize === ProfileImageSize.Small,
    [styles.large]: avatarSize === ProfileImageSize.Large,
  };

  const wrapperClasses = {
    [styles.wrapper]: true,
    [styles.fullWidth]: profileLayout === ProfileLayout.FullWidth,
    [styles.card]: profileLayout === ProfileLayout.Card,
    [styles.mobile]: isMobile,
    [styles.squareOverlay]: isAvatarSquare,
  };

  const avatarClasses = {
    [styles.avatar]: true,
    [styles.squareAvatar]: isAvatarSquare,
  };

  return (
    <div className={classNames(sizeClasses)}>
      <div className={classNames(wrapperClasses, className)}>
        <Avatar
          name={name}
          src={imageURL}
          data-hook={DataHook.ProfilePhoto}
          className={classNames(avatarClasses)}
        />
        {canUpload && (
          <div>
            <input
              id={inputId}
              className={styles.uploadInput}
              type="file"
              accept={acceptableImageExtensions}
              aria-label={t('profile-widget.change-profile-photo')}
              onChange={onClick}
            />
            <label htmlFor={inputId} className={styles.uploadLabel} />
            {!isMobile && (
              <div className={styles.pictureWrapper}>
                <div className={styles.iconOval}>
                  <Camera />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarInput;
